/**
 * VoBizDevice — drop-in replacement for Twilio Device when using India (VoBiz) calling.
 *
 * Wraps SIP.js to expose the same surface as Twilio Voice SDK so DialerSliderCard.js
 * needs minimal changes: same device.connect(), call.on(), call.disconnect(), call.mute(), etc.
 *
 * Requires: npm install sip.js
 */

import {
  UserAgent,
  Registerer,
  Inviter,
  SessionState,
  RegistererState
} from "sip.js";

// ---------------------------------------------------------------------------
// VoBizCallSession — wraps an SIP.js Inviter to look like a Twilio Call object
// ---------------------------------------------------------------------------
class VoBizCallSession {
  constructor(inviter) {
    this.inviter = inviter;
    this._listeners = {};
    this._callStatus = "connecting"; // mirrors Twilio: open / closed / ringing / connecting
    this.callSid = null; // set externally after backend logs the call

    inviter.stateChange.addListener(state => this._onStateChange(state));
  }

  _onStateChange(state) {
    switch (state) {
      case SessionState.Establishing:
        this._callStatus = "ringing";
        // Start polling for peer connection so we can hook ontrack for early media (183 ringback)
        this._watchForEarlyMedia();
        break;

      case SessionState.Established:
        this._callStatus = "open";
        this._attachRemoteAudio();
        this._emit("accept", this._makeConnProxy());
        break;

      case SessionState.Terminated:
        this._callStatus = "closed";
        this._emit("disconnect", this._makeConnProxy());
        break;

      default:
        break;
    }
  }

  /** Mimics the Twilio connection object passed to event handlers */
  _makeConnProxy() {
    return {
      status: () => this._callStatus,
      mediaStream: { CallSid: this.callSid },
      parameters: { CallSid: this.callSid },
      mute: (val) => this.mute(val),
      unmute: () => this.unmute(),
      isMuted: () => this.isMuted(),
      disconnect: () => this.disconnect(),
      sendDigits: (digits) => this.sendDigits(digits),
      getStats: async () => {
        try {
          const pc = this.inviter?.sessionDescriptionHandler?.peerConnection;
          if (pc) return await pc.getStats();
        } catch (_) {}
        return [];
      }
    };
  }

  // ---- Twilio Call-compatible API ----------------------------------------

  on(event, handler) {
    if (!this._listeners[event]) this._listeners[event] = [];
    this._listeners[event].push(handler);
    return this;
  }

  async disconnect() {
    try {
      if (this.inviter.state === SessionState.Establishing) {
        await this.inviter.cancel();
      } else if (this.inviter.state === SessionState.Established) {
        await this.inviter.bye();
      }
    } catch (e) {
      console.error("[VoBiz] disconnect error:", e);
    }
  }

  /**
   * Supports both mute() and mute(true/false) to match Twilio SDK variants.
   */
  mute(value) {
    const shouldMute = value !== false; // undefined / true → mute; false → unmute
    this._setAudioEnabled(!shouldMute);
  }

  unmute() {
    this._setAudioEnabled(true);
  }

  isMuted() {
    try {
      const pc = this.inviter?.sessionDescriptionHandler?.peerConnection;
      if (pc) {
        const sender = pc.getSenders().find(s => s.track?.kind === "audio");
        return sender ? !sender.track.enabled : false;
      }
    } catch (_) {}
    return false;
  }

  sendDigits(digits) {
    try {
      // Prefer WebRTC DTMF sender
      const pc = this.inviter?.sessionDescriptionHandler?.peerConnection;
      if (pc) {
        const dtmfSender = pc.getSenders().find(s => s.dtmf);
        if (dtmfSender?.dtmf) {
          dtmfSender.dtmf.insertDTMF(String(digits));
          return;
        }
      }
      // Fallback: SIP INFO method
      this.inviter.info({
        requestOptions: {
          body: {
            contentDisposition: "render",
            contentType: "application/dtmf-relay",
            content: `Signal=${digits}\r\nDuration=100`
          }
        }
      });
    } catch (e) {
      console.error("[VoBiz] sendDigits error:", e);
    }
  }

  /** Returns Twilio-compatible status string */
  status() {
    return this._callStatus;
  }

  // ---- Internal helpers ---------------------------------------------------

  /** Poll for the RTCPeerConnection and attach audio as soon as any remote track arrives (early media / 183) */
  _watchForEarlyMedia() {
    const tryAttach = () => {
      if (this._callStatus === "open" || this._callStatus === "closed") return; // already handled
      const pc = this.inviter?.sessionDescriptionHandler?.peerConnection;
      if (pc) {
        // Hook ontrack so we attach the moment a remote track arrives
        pc.addEventListener("track", () => {
          if (this._callStatus === "ringing") {
            this._attachRemoteAudio();
          }
        });
        // Also try immediately in case tracks are already present
        this._attachRemoteAudio();
      } else {
        // PC not ready yet — retry in 50ms
        setTimeout(tryAttach, 50);
      }
    };
    setTimeout(tryAttach, 50);
  }

  _attachRemoteAudio() {
    try {
      const pc = this.inviter?.sessionDescriptionHandler?.peerConnection;
      if (!pc) return;
      const remoteStream = new MediaStream();
      pc.getReceivers().forEach(receiver => {
        if (receiver.track) remoteStream.addTrack(receiver.track);
      });
      let audio = document.getElementById("vobiz-remote-audio");
      if (!audio) {
        audio = document.createElement("audio");
        audio.id = "vobiz-remote-audio";
        audio.autoplay = true;
        document.body.appendChild(audio);
      }
      audio.srcObject = remoteStream;
      console.log("[VoBiz] Remote audio attached, tracks:", remoteStream.getTracks().length);
    } catch (e) {
      console.error("[VoBiz] _attachRemoteAudio error:", e);
    }
  }

  _setAudioEnabled(enabled) {
    try {
      const pc = this.inviter?.sessionDescriptionHandler?.peerConnection;
      if (pc) {
        pc.getSenders().forEach(sender => {
          if (sender.track && sender.track.kind === "audio") {
            sender.track.enabled = enabled;
          }
        });
      }
    } catch (e) {
      console.error("[VoBiz] mute/unmute error:", e);
    }
  }

  _emit(event, ...args) {
    (this._listeners[event] || []).forEach(h => {
      try { h(...args); } catch (e) { console.error("[VoBiz] event handler error:", e); }
    });
  }
}

// ---------------------------------------------------------------------------
// VoBizDevice — wraps SIP.js UserAgent to look like a Twilio Device object
// ---------------------------------------------------------------------------
class VoBizDevice {
  constructor() {
    this.state = "unregistered"; // registered / registering / unregistered / destroyed
    this._isVoBiz = true;        // distinguishes VoBizDevice from Twilio Device in guards
    this._userAgent = null;
    this._registerer = null;
    this._sipDomain = null;
    this._listeners = {};
    this._activeSession = null;
    // Stub audio property so DialerSliderCard's device.audio.on() call doesn't crash
    this.audio = { on: () => {} };
  }

  /**
   * Initialises SIP.js and registers with VoBiz.
   * @param {string} sipUsername  SIP endpoint credential username (for auth)
   * @param {string} sipPassword  SIP endpoint credential password
   * @param {string} sipDomain    e.g. "registrar.vobiz.ai"
   * @param {string} wsServer     e.g. "wss://registrar.vobiz.ai:5063"
   * @param {string} fromNumber   The purchased India DID, e.g. "+918046733512".
   *                              Used as the SIP AOR so VoBiz maps outbound CLI correctly.
   *                              VoBiz ignores P-Preferred-Identity; the AOR is authoritative.
   */
  async init(sipUsername, sipPassword, sipDomain, wsServer, fromNumber) {
    this._sipDomain = sipDomain;
    this.state = "registering";
    const resolvedWsServer = wsServer || `wss://${sipDomain}`;

    // Use the purchased DID as the SIP Address-of-Record (AOR).
    // VoBiz determines outbound CLI from the registered AOR, not from custom headers.
    // Strip non-digit characters: "+918046733512" → "918046733512"
    const aorUser = fromNumber ? fromNumber.replace(/[^\d]/g, "") : sipUsername;
    console.log(`[VoBiz] init: sipUsername=${sipUsername} aorUser=${aorUser} sipDomain=${sipDomain} wsServer=${resolvedWsServer}`);

    try {
      const sipUri = `sip:${aorUser}@${sipDomain}`;
      console.log(`[VoBiz] init: making URI from ${sipUri}`);
      const uri = UserAgent.makeURI(sipUri);
      if (!uri) throw new Error("Invalid SIP URI: " + sipUri);

      this._userAgent = new UserAgent({
        uri,                              // AOR = DID number (918046733512@registrar.vobiz.ai)
        transportOptions: {
          server: resolvedWsServer
        },
        authorizationUsername: sipUsername, // endpoint credentials used for Digest auth
        authorizationPassword: sipPassword,
        logLevel: "debug",
        sessionDescriptionHandlerFactoryOptions: {
          constraints: { audio: true, video: false },
          peerConnectionConfiguration: {
            iceServers: [{ urls: "stun:stun.l.google.com:19302" }]
          }
        }
      });

      console.log(`[VoBiz] init: starting UserAgent (connecting to ${resolvedWsServer})...`);
      await this._userAgent.start();
      console.log(`[VoBiz] init: UserAgent started successfully`);

      this._registerer = new Registerer(this._userAgent);
      this._registerer.stateChange.addListener(regState => {
        console.log(`[VoBiz] Registerer state changed: ${regState}`);
        if (regState === RegistererState.Registered) {
          console.log(`[VoBiz] ✅ SIP Registration SUCCESSFUL as ${sipUsername}@${sipDomain}`);
          this.state = "registered";
          this._emit("registered");
        } else if (regState === RegistererState.Unregistered) {
          console.warn(`[VoBiz] ⚠️ SIP Unregistered`);
          this.state = "unregistered";
          this._emit("unregistered");
        }
      });

      console.log(`[VoBiz] init: sending REGISTER...`);
      await this._registerer.register();
      console.log(`[VoBiz] init: REGISTER sent`);
    } catch (e) {
      console.error(`[VoBiz] init: FAILED`, e);
      this.state = "unregistered";
      this._emit("error", e);
      throw e;
    }
  }

  /**
   * Dials toNumber via VoBiz SIP.
   * Accepts the same params object as Twilio device.connect().
   */
  async connect(arg) {
    // Twilio SDK wraps params as { params: {...} } — unwrap if needed
    const params = (arg && arg.params) ? arg.params : arg;
    const { to_number, caller_id, memberId, randomNo, task_id, contactId } = params;

    // Strip all non-digit chars from destination and caller-ID to produce clean E.164 user-parts.
    // VoBiz registered AOR uses digits-only (e.g. "918046733512"), so the P-Preferred-Identity
    // must match that format — keeping the "+" sign causes a mismatch and results in 486 Busy.
    const cleanNumber = String(to_number).replace(/[^\d]/g, "");
    // Keep leading "+" for destination if present (PSTN routing understands both; strip + for AOR match)
    const toNumberForSip = cleanNumber; // digits-only: 919443858039
    const callerIdDigits = caller_id ? String(caller_id).replace(/[^\d]/g, "") : ""; // 918046733512

    console.log("[VoBiz] connect: to_number=", to_number, "→ SIP user=", toNumberForSip, "caller_id=", caller_id, "→ AOR user=", callerIdDigits);

    const target = UserAgent.makeURI(`sip:${toNumberForSip}@${this._sipDomain}`);
    if (!target) throw new Error(`[VoBiz] Invalid target number: ${toNumberForSip}`);

    const inviter = new Inviter(this._userAgent, target, {
      sessionDescriptionHandlerOptions: {
        constraints: { audio: true, video: false }
      },
      extraHeaders: [
        // P-Preferred-Identity must match the registered AOR (digits-only, no "+").
        // VoBiz maps this to the purchased DID for outbound CLI.
        `P-Preferred-Identity: <sip:${callerIdDigits}@${this._sipDomain}>`,
        `X-Caller-Id: ${caller_id || ""}`,
        `X-Member-Id: ${memberId || ""}`,
        `X-Random-No: ${randomNo || ""}`,
        `X-Task-Id: ${task_id || ""}`,
        `X-Contact-Id: ${contactId || ""}`
      ]
    });

    const session = new VoBizCallSession(inviter);
    session.callSid = randomNo || null;
    this._activeSession = session;

    await inviter.invite();
    return session;
  }

  disconnectAll() {
    if (this._activeSession) {
      this._activeSession.disconnect();
      this._activeSession = null;
    }
  }

  destroy() {
    try {
      this.disconnectAll();
      if (this._registerer) this._registerer.unregister();
      if (this._userAgent) this._userAgent.stop();
    } catch (e) {
      console.error("[VoBiz] destroy error:", e);
    }
    this.state = "destroyed";
  }

  // Stubs for token-related events Twilio emits but VoBiz doesn't need
  updateToken() {}

  on(event, handler) {
    if (!this._listeners[event]) this._listeners[event] = [];
    this._listeners[event].push(handler);
    return this;
  }

  once(event, handler) {
    const wrapper = (...args) => {
      this.off(event, wrapper);
      handler(...args);
    };
    return this.on(event, wrapper);
  }

  off(event, handler) {
    if (this._listeners[event]) {
      this._listeners[event] = this._listeners[event].filter(h => h !== handler);
    }
    return this;
  }

  /**
   * Re-register with VoBiz SIP — called by ensureDeviceReady() when device
   * is in "unregistered" state before a call attempt.
   */
  async register() {
    if (!this._registerer) return;
    try {
      this.state = "registering";
      await this._registerer.register();
    } catch (e) {
      this.state = "unregistered";
      console.error("[VoBiz] register() error:", e);
      throw e;
    }
  }

  _emit(event, ...args) {
    (this._listeners[event] || []).forEach(h => {
      try { h(...args); } catch (e) { console.error("[VoBiz] device event error:", e); }
    });
  }
}

export default VoBizDevice;
export { VoBizDevice };
